import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend, ComposedChart, Area
} from 'recharts';

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

export default function Reports() {
  const [occupancy, setOccupancy] = useState([]);
  const [revenueByProp, setRevenueByProp] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [sources, setSources] = useState([]);
  const [topUnits, setTopUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState('6');
  const [filterProp, setFilterProp] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { months: Number(months) };
      const propParam = filterProp !== 'all' ? { ...params, property_id: filterProp } : params;

      const [occ, revProp, monthly, src, top, props] = await Promise.all([
        api.get('/reports/occupancy', { params: propParam }),
        api.get('/reports/revenue-by-property', { params }),
        api.get('/reports/monthly-revenue', { params: propParam }),
        api.get('/reports/booking-sources', { params }),
        api.get('/reports/top-units', { params }),
        api.get('/properties'),
      ]);

      setOccupancy(occ.data);
      setRevenueByProp(revProp.data);
      setMonthlyRevenue(monthly.data);
      setSources(src.data);
      setTopUnits(top.data);
      setProperties(props.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [months, filterProp]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium text-slate-900 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="text-xs">
            {p.name}: {typeof p.value === 'number' ? (p.name.includes('Rate') || p.name.includes('rate') ? `${p.value}%` : `R${p.value.toLocaleString()}`) : p.value}
          </p>
        ))}
      </div>
    );
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;

  return (
    <div data-testid="reports-page">
      <div className="page-header">
        <h1 className="page-title">Reports & Analytics</h1>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Select value={filterProp} onValueChange={setFilterProp}>
            <SelectTrigger className="w-full sm:w-48" data-testid="report-property-filter"><SelectValue placeholder="All Properties" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Properties</SelectItem>
              {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="w-full sm:w-36" data-testid="report-months-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Last 3 months</SelectItem>
              <SelectItem value="6">Last 6 months</SelectItem>
              <SelectItem value="12">Last 12 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="occupancy" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="occupancy" data-testid="report-tab-occupancy">Occupancy</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="report-tab-revenue">Revenue</TabsTrigger>
          <TabsTrigger value="sources" data-testid="report-tab-sources">Sources</TabsTrigger>
          <TabsTrigger value="performance" data-testid="report-tab-performance">Performance</TabsTrigger>
        </TabsList>

        {/* Occupancy Tab */}
        <TabsContent value="occupancy">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {occupancy.length > 0 && (
              <>
                <div className="stat-card" data-testid="report-avg-occupancy">
                  <p className="text-xs text-slate-500 mb-1">Average Occupancy</p>
                  <p className="text-3xl font-semibold font-heading text-slate-900">
                    {(occupancy.reduce((s, o) => s + o.occupancy_rate, 0) / occupancy.length).toFixed(1)}%
                  </p>
                </div>
                <div className="stat-card" data-testid="report-peak-occupancy">
                  <p className="text-xs text-slate-500 mb-1">Peak Occupancy</p>
                  <p className="text-3xl font-semibold font-heading text-emerald-600">
                    {Math.max(...occupancy.map(o => o.occupancy_rate))}%
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{occupancy.find(o => o.occupancy_rate === Math.max(...occupancy.map(x => x.occupancy_rate)))?.month_label}</p>
                </div>
                <div className="stat-card" data-testid="report-total-bookings">
                  <p className="text-xs text-slate-500 mb-1">Total Bookings</p>
                  <p className="text-3xl font-semibold font-heading text-blue-600">
                    {occupancy.reduce((s, o) => s + o.reservations_count, 0)}
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="occupancy-trend-chart">
            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">Occupancy Trend</h3>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={occupancy}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month_label" tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#64748b' }} unit="%" />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#64748b' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area yAxisId="left" type="monotone" dataKey="occupancy_rate" name="Occupancy Rate" fill="#2563EB20" stroke="#2563EB" strokeWidth={2} />
                <Bar yAxisId="right" dataKey="reservations_count" name="Bookings" fill="#10B981" radius={[4, 4, 0, 0]} barSize={30} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly occupancy table */}
          <div className="table-wrap mt-6">
            <table className="w-full data-table min-w-[550px]">
              <thead><tr><th>Month</th><th>Units</th><th>Occupied Days</th><th>Total Days</th><th>Occupancy Rate</th><th>Bookings</th></tr></thead>
              <tbody>
                {occupancy.map(o => (
                  <tr key={o.month}>
                    <td className="font-medium text-slate-900">{o.month_label}</td>
                    <td>{o.total_units}</td>
                    <td>{o.occupied_days}</td>
                    <td>{o.total_unit_days}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-slate-100 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${Math.min(o.occupancy_rate, 100)}%` }} />
                        </div>
                        <span className="text-sm font-medium">{o.occupancy_rate}%</span>
                      </div>
                    </td>
                    <td>{o.reservations_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Revenue Tab */}
        <TabsContent value="revenue">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Revenue by Property */}
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="revenue-by-property-chart">
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">Revenue by Property</h3>
              {revenueByProp.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={revenueByProp} dataKey="total_revenue" nameKey="property_name" cx="50%" cy="50%" outerRadius={90} innerRadius={40} label={({ name, value }) => `R${(value/1000).toFixed(0)}k`}>
                        {revenueByProp.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => `R${v.toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2">
                    {revenueByProp.map((r, i) => (
                      <div key={r.property_id} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className="text-slate-700">{r.property_name}</span>
                        </div>
                        <span className="font-semibold text-slate-900">R{r.total_revenue.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : <p className="text-sm text-slate-400">No revenue data</p>}
            </div>

            {/* Monthly Income vs Expense */}
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="income-expense-chart">
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">Income vs Expenses</h3>
              {monthlyRevenue.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlyRevenue}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                    <Tooltip formatter={(v) => `R${v.toLocaleString()}`} />
                    <Legend />
                    <Bar dataKey="income" name="Income" fill="#10B981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expense" name="Expense" fill="#EF4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-slate-400">No data available</p>}
            </div>
          </div>

          {/* Net revenue trend */}
          {monthlyRevenue.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="net-revenue-chart">
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">Net Revenue Trend</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthlyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip formatter={(v) => `R${v.toLocaleString()}`} />
                  <Legend />
                  <Line type="monotone" dataKey="net" name="Net Revenue" stroke="#2563EB" strokeWidth={2} dot={{ fill: '#2563EB', r: 4 }} />
                  <Line type="monotone" dataKey="income" name="Income" stroke="#10B981" strokeWidth={1.5} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </TabsContent>

        {/* Sources Tab */}
        <TabsContent value="sources">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="sources-breakdown-chart">
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">Booking Source Distribution</h3>
              {sources.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={sources} dataKey="count" nameKey="_id" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                      {sources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-slate-400">No source data</p>}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="sources-table">
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">Source Breakdown</h3>
              <div className="space-y-3">
                {sources.map((s, i) => {
                  const total = sources.reduce((sum, x) => sum + x.count, 0);
                  const pct = total > 0 ? ((s.count / total) * 100).toFixed(1) : 0;
                  return (
                    <div key={s._id} className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-slate-900">{s._id || 'unknown'}</span>
                          <span className="text-slate-500">{s.count} bookings ({pct}%)</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div className="h-2 rounded-full" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance">
          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="top-units-chart">
            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">Top Performing Units</h3>
            {topUnits.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topUnits} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} />
                    <YAxis type="category" dataKey="unit_name" width={140} tick={{ fontSize: 12, fill: '#64748b' }} />
                    <Tooltip />
                    <Bar dataKey="bookings" name="Bookings" fill="#2563EB" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-6">
                  <table className="w-full data-table">
                    <thead><tr><th>Rank</th><th>Unit</th><th>Bookings</th><th>Nightly Rate</th></tr></thead>
                    <tbody>
                      {topUnits.map((u, i) => (
                        <tr key={u.unit_id}>
                          <td><Badge className={i < 3 ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}>#{i + 1}</Badge></td>
                          <td className="font-medium text-slate-900">{u.unit_name}</td>
                          <td>{u.bookings}</td>
                          <td>R{(u.base_price || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : <p className="text-sm text-slate-400">No performance data yet</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
